// The three guaranteed app types, as PLANS.
//
// These exist so the plan screen can paint before any network call: the classifier picks
// a type from the raw idea and the scaffold below is a complete, buildable plan on its
// own. The model pass then refines it in place. If that pass fails, times out, or the
// user is offline, what is already on screen still builds.
//
// This owns the plan-side scaffolds only. The hand-tuned GENERATION prompts for the same
// three types are a separate concern and inherit `classifyIdea` from here.

import type { AppPlan, AppType, PlanChart, PlanFeature, PlanScreen } from './planTypes';
import { slugify } from './planTypes';

export interface StarterPrompt {
  /** Shown on the chip. */
  label: string;
  /** Sent as the idea — specific enough to produce something good on its own. */
  prompt: string;
}

/**
 * What the front door offers, drawn from the twenty-two apps this engine was built for.
 *
 * Deliberately specific: "an event app" produces mush, "RSVP for a birthday dinner"
 * produces an app. And deliberately spread across what the product can actually do —
 * server-held capacity, content the owner changes after publishing, accounts, a mailing
 * list, AI — because a starter set that only shows forms teaches people the product only
 * makes forms.
 *
 * Ordered by how quickly someone recognises their own situation in one.
 */
export const STARTER_PROMPTS: StarterPrompt[] = [
  // Catalog: several dated things, each with its own limit. Seven of the twenty-two.
  { label: 'Class timetable', prompt: 'A timetable for the classes I teach — each class has sessions on set dates with a limited number of places, and people book one' },
  { label: 'Volunteer shifts', prompt: 'A shift board for our food bank, with shifts on set days, how many people each needs, and volunteers claiming one' },
  { label: 'Staff rota', prompt: 'A rota for my cafe — I post the week\'s shifts, staff sign in and claim one, and a full shift stops taking people' },

  // Collecting, with a real deadline the server holds.
  { label: 'Birthday RSVP', prompt: 'An RSVP page for a birthday dinner, with a guest count, a meal choice and a reply deadline' },
  { label: 'Waiting list', prompt: 'A one-page pitch for something I am launching, with an email waiting list people confirm by clicking a link' },

  // Accounts: the person's own data, on any device.
  { label: 'Client intake', prompt: 'An intake form for my clients, and an account where each one signs in to see what stage their job is at' },

  // The person's own record, no account needed.
  { label: 'Workout log', prompt: 'A weekly workout log that tracks sets, reps and personal bests' },

  // AI, doing something a form cannot.
  { label: 'Home inventory', prompt: 'An app where I photograph each room and it lists what is in it with rough values, for my home insurance' },
];

// Scoring beats first-match: "a signup page for a 5k fun run" hits both `signup` and
// `run`, and the intent is the signup.
/**
 * The catalog shape: an owner publishes dated things with limited places, and visitors
 * take one of them. Seven of the selected apps are this — classes, shifts, rotas, supper
 * nights, desks, side-events — and they all fail the same way without server-enforced
 * capacity: two people take the last place and both are told yes.
 *
 * Scored ahead of `rsvp` and `request` because the words overlap heavily ("book", "event",
 * "signup") and the distinguishing feature is that there are SEVERAL things to choose
 * between, each with its own limit.
 */
const CATALOG_WORDS = [
  'each', 'several', 'multiple', 'sessions', 'session', 'shifts', 'shift', 'classes',
  'class', 'nights', 'rota', 'timetable', 'schedule of', 'slots', 'slot', 'places',
  'seats', 'capacity', 'limited', 'per week', 'weekly', 'sold out', 'full', 'headcount',
  'desks', 'rooms', 'pitches',
];

/**
 * Words that say a thing is SCARCE, which is itself evidence that people take it.
 *
 * The catalog shape used to require an explicit verb — book, enrol, reserve — and the most
 * natural way to describe one of these apps does not contain one: "a timetable for the
 * classes I teach, with sessions on set dates and limited places" says everything about
 * limited places and never says "book". That fell through to a general app: nothing
 * collected, no capacity, and the limit the person actually asked for was decorative.
 */
const SCARCITY_WORDS = [
  'places', 'seats', 'capacity', 'limited', 'spots', 'sold out', 'headcount', 'slots',
  'slot', 'full', 'max ', 'maximum', 'up to',
];

const KEYWORDS: Record<'rsvp' | 'request' | 'tracker', string[]> = {
  rsvp: [
    'rsvp', 'invite', 'invitation', 'wedding', 'birthday', 'party', 'event', 'guest',
    'guests', 'attending', 'attend', 'ceremony', 'reception', 'anniversary', 'shower',
    'save the date', 'who is coming', 'celebration', 'housewarming', 'dinner party',
  ],
  request: [
    'booking', 'book a', 'appointment', 'reserve', 'reservation', 'request', 'requests',
    'signup', 'sign up', 'sign-up', 'waitlist', 'wait list', 'enquiry', 'inquiry',
    'contact form', 'apply', 'application', 'intake', 'quote', 'order form', 'register',
    'registration', 'collect emails', 'mailing list', 'waiting list', 'subscribe',
    'early access', 'notify me', 'when it opens', 'launch page', 'email box',
  ],
  tracker: [
    'track', 'tracker', 'tracking', 'log', 'logger', 'habit', 'workout', 'exercise',
    'gym', 'lifting', 'running', 'reading', 'water', 'budget', 'expense', 'spending',
    'packing', 'checklist', 'to-do', 'todo', 'gift list', 'streak', 'progress',
    'journal', 'diary', 'inventory', 'collection', 'count',
  ],
};

/**
 * Whether an idea is about the speaker's OWN records rather than anyone else's.
 *
 * Collecting needs somebody other than the owner to submit something. An idea written
 * throughout in the first person — "I film MY swing, it tells ME, show MY scores" — has
 * nobody to collect from, however many booking-adjacent words it happens to contain.
 *
 * This exists because a golf swing coach classified as an RSVP off the word "coming" in
 * "whether the same fault keeps coming back". It was handed a submissions collection it had
 * no use for, and then refused by the gate rule that insists a collecting app must call
 * S.submit — unbuildable, because of one keyword.
 *
 * Two markers, not one: "a waitlist for MY supper club" is first person and is still
 * collecting. A word naming other people settles it immediately the other way.
 */
const OTHERS_WORDS = [
  'guest', 'guests', 'visitor', 'visitors', 'people', 'anyone', 'everyone', 'volunteer',
  'volunteers', 'student', 'students', 'member', 'members', 'customer', 'customers',
  'client', 'clients', 'attendee', 'attendees', 'friend', 'friends', 'family', 'staff',
  'they', 'them', 'their', 'someone', 'others', 'public', 'trader', 'traders', 'parent',
  'parents', 'subscriber', 'subscribers',
];

const SELF_MARKERS = [' i ', ' my ', ' me ', ' mine ', " i'"];

function isAboutOnlyMe(text: string): boolean {
  if (OTHERS_WORDS.some((w) => text.includes(` ${w} `) || text.includes(` ${w},`) || text.includes(` ${w}.`))) {
    return false;
  }
  const hits = SELF_MARKERS.reduce((n, m) => n + text.split(m).length - 1, 0);
  return hits >= 2;
}

export function classifyIdea(idea: string): AppType {
  const text = ` ${String(idea || '').toLowerCase()} `;

  // Nobody else in the idea means nothing to collect FROM: fall through to the personal
  // shapes rather than handing the app a submissions collection it will never use.
  const onlyMe = isAboutOnlyMe(text);

  // Catalog first: it is a strictly richer shape than rsvp or request and its vocabulary
  // sits on top of theirs. Two hits are required so one stray "each" cannot promote a
  // plain signup form into a booking system with capacity it never wanted.
  const catalogHits = CATALOG_WORDS.reduce((n, kw) => (text.includes(kw) ? n + 1 : n), 0);
  const collecting = /book|rsvp|sign\s?up|enrol|enroll|claim|reserve|attend|volunteer/.test(text);
  // Scarcity counts as taking: a thing described as limited is a thing people take, even
  // when the description never says the verb.
  const scarce = SCARCITY_WORDS.some((kw) => text.includes(kw));
  if (catalogHits >= 2 && (collecting || scarce)) return 'catalog';

  let best: AppType = 'general';
  let bestScore = 0;
  const scored = onlyMe ? (['tracker'] as const) : (['rsvp', 'request', 'tracker'] as const);
  for (const type of scored) {
    const score = KEYWORDS[type].reduce((n, kw) => (text.includes(kw) ? n + 1 : n), 0);
    if (score > bestScore) {
      best = type;
      bestScore = score;
    }
  }
  return best;
}

const feature = (label: string, detail: string, included = true): PlanFeature => ({
  id: slugify(label),
  label,
  detail,
  included,
  origin: 'scaffold',
});

const screen = (label: string, purpose: string): PlanScreen => ({ id: slugify(label), label, purpose });

const chart = (label: string, kind: PlanChart['kind'], source: string): PlanChart => ({
  id: slugify(label),
  label,
  kind,
  source,
  included: true,
});

/**
 * A readable name from the raw idea, for the ~100ms before the model proposes a better
 * one. Strips the leading article and the "a page/app/site for" throat-clearing, then
 * takes the first few words.
 */
/** Words that must never end a name — truncating on one is what produced "Signup page for
 *  a" and "A Daily Journal Where". */
const TRAILING_FILLER = /^(a|an|the|for|to|with|and|or|of|in|on|at|by|that|where|which|my|your|our|per)$/i;

/**
 * A usable app name from the user's one-line idea.
 *
 * The old version took the first four words verbatim, so real prompts became "Signup page
 * for a" and "A Daily Journal Where" — cut mid-phrase and ending on an article. Names are
 * the first thing shown in the list and on the published page, so they have to read like
 * something a person would write.
 *
 * The rewrite does three things: stop at the first clause boundary, put the SUBJECT first
 * when the idea has the very common "<kind> page for <subject>" shape (so "signup page for
 * a 5k fun run" becomes "5k fun run signup", not "Signup page"), and never end on a
 * function word.
 */
export function nameFromIdea(idea: string): string {
  const cleaned = String(idea || '')
    .replace(/^\s*(a|an|the)\s+/i, '')
    .replace(/^\s*(simple|basic|small|quick)\s+/i, '')
    .replace(/[,.;!?].*$/s, '')       // one clause only — the rest is detail, not a name
    .trim();
  if (!cleaned) return 'My app';

  const shaped = /^(.+?)\s+(page|app|form|site|website|tracker|log|list|planner)\s+(?:for|to)\s+(?:a|an|the)?\s*(.+)$/i
    .exec(cleaned);
  const phrase = shaped ? `${shaped[3]} ${shaped[1]}` : cleaned;

  const words = phrase.split(/\s+/).filter(Boolean).slice(0, 4);
  while (words.length > 1 && TRAILING_FILLER.test(words[words.length - 1])) words.pop();
  if (!words.length) return 'My app';

  const title = words.join(' ');
  return title.charAt(0).toUpperCase() + title.slice(1);
}

/**
 * Every scaffold names three kits, spanning three families, before any model has spoken.
 *
 * The wizard's look step paints on first frame from this scaffold, and a step that opened
 * with three identical placeholder cards would undo the thing it exists to show. The model's
 * `kitRanking` replaces this when the tailoring call lands; until then these are a real,
 * subject-appropriate choice rather than a holding pattern.
 */
function baseFor(type: AppType): Omit<AppPlan, 'appName' | 'type'> {
  switch (type) {
    case 'catalog':
      return {
        tagline: 'Pick one, and know it is really yours',
        kitId: 'ledger',
        kitRanking: ['ledger', 'editorial', 'gallery'],
        keeps: null,
        layout: null,
        features: [
          feature('What is coming up', 'the list of dated items with date, detail and places left'),
          feature('One item in full', 'everything about the one they tapped, and the action to take it'),
          feature('Take a place', 'the form: who they are, how to reach them, anything the owner needs'),
          feature('Places left', 'a live count per item, and a full state that stops accepting'),
          feature('Confirmation', 'what they took, when, and what happens next'),
          feature('Past items drop away', 'anything already gone is not offered, computed from new Date()'),
          feature('Waiting list when full', 'take a name anyway and say plainly it is a waiting list', false),
          feature('What I have taken', 'a signed-in person sees their own bookings', false),
        ],
        screens: [
          screen('Upcoming', 'opens here — every item still open, soonest first'),
          screen('Details', 'one item in full, with the action'),
          screen('Take', 'the form itself'),
          screen('Confirmed', 'what was taken and what happens next'),
        ],
        // Write-only collection: a count is the only honest number on the page, and it
        // comes from the server via countBy, never from anything the page can read.
        charts: [],
        collects: { collection: 'bookings', fields: ['name', 'email', 'itemId', 'notes'] },
        seedNote: '',
        constraints: [],
        rules: {
          capacity: { groupBy: 'itemId', limit: 20 },
          uniqueBy: 'email',
          overflow: 'reject',
          countBy: 'itemId',
        },
        // The dated things themselves are the OWNER's, and they change after publishing —
        // a night is added, a session moves. Held on the server so that costs no rebuild.
        manages: [{ collection: 'items', fields: ['name', 'startsAt', 'places', 'details'] }],
      };
    case 'rsvp':
      return {
        tagline: 'Everything guests need, and one tap to reply',
        kitId: 'editorial',
        kitRanking: ['editorial', 'notebook', 'soft'],
        keeps: null,
        layout: null,
        features: [
          feature('Reply yes or no', 'the reply itself, as the primary action on the page'),
          feature('Event details', 'date, time, place and what to expect'),
          feature('Plus-ones', 'how many people the guest is bringing'),
          feature('Meal choice', 'a choice per guest, shown only after they say yes'),
          feature('Reply deadline', 'the date shown clearly, with a note when it has passed'),
          feature('Note to the host', 'a short free-text message', false),
          feature('Directions and parking', 'how to get there, and where to leave the car', false),
        ],
        screens: [
          screen('Invitation', 'the occasion, the details, and the ask'),
          screen('Reply', 'the RSVP form itself'),
          screen('Thanks', 'confirmation of what was sent, and what happens next'),
        ],
        // Write-only collection: there is no honest chart to draw. See planTypes.
        charts: [],
        collects: { collection: 'rsvps', fields: ['name', 'attending', 'guests', 'meal', 'note'] },
        seedNote: '',
        constraints: [],
        rules: null,
        manages: [],
      };
    case 'request':
      return {
        tagline: 'Ask for what you need, in one short form',
        kitId: 'ledger',
        kitRanking: ['ledger', 'notebook', 'gallery'],
        keeps: null,
        layout: null,
        features: [
          feature('Request form', 'name, contact and the details of the request'),
          feature('What is on offer', 'the services or options, so the visitor knows what they are asking for'),
          feature('Preferred date and time', 'when they would like it'),
          feature('Confirmation', 'a real confirmation state after a successful send'),
          feature('What happens next', 'how and when they will hear back'),
          feature('Notes or special requirements', 'free text for anything unusual', false),
        ],
        screens: [
          screen('Overview', 'what this is and what can be requested'),
          screen('Request', 'the form'),
          screen('Sent', 'confirmation and next steps'),
        ],
        charts: [],
        collects: { collection: 'requests', fields: ['name', 'email', 'preferred', 'details'] },
        seedNote: '',
        constraints: [],
        rules: null,
        manages: [],
      };
    case 'tracker':
      return {
        tagline: 'Keep the record, see the shape of it',
        kitId: 'sport',
        kitRanking: ['sport', 'ledger', 'soft'],
        // A tracker is the shape this exists for: months of entries the person is building
        // up for themselves, which must follow them to a new phone.
        keeps: {
          collection: 'entries',
          fields: [
            { name: 'loggedAt', type: 'date' },
            { name: 'value', type: 'number' },
            { name: 'note', type: 'string' },
          ],
        },
        layout: null,
        features: [
          feature('Add an entry', 'the fastest possible path to recording one thing'),
          feature('History', 'everything recorded, newest first, grouped by day'),
          feature('Edit and delete', 'fix a mistake, with a confirmation before deleting'),
          feature('Progress against a goal', 'a target and how close today is to it'),
          feature('Streak', 'consecutive days, and what breaks it'),
          feature('Filter by period', 'this week, this month, everything', false),
          feature('Notes on an entry', 'a short note attached to a record', false),
        ],
        screens: [
          screen('Today', 'what matters now, and the add action'),
          screen('History', 'the full record'),
          screen('Detail', 'one entry, with its numbers and notes'),
        ],
        charts: [
          chart('Last 7 days', 'bars', 'one bar per day from the recorded entries'),
          chart('Goal progress', 'ring', 'this period against the target'),
          chart('Streak grid', 'heat', 'one cell per day, filled when there is an entry'),
        ],
        collects: null,
        seedNote: '6-12 realistic entries spread across the last three weeks, dated from new Date()',
        constraints: [],
        rules: null,
        manages: [],
      };
    default:
      return {
        tagline: 'Small, useful, and yours',
        kitId: 'soft',
        kitRanking: ['soft', 'editorial', 'arcade'],
        keeps: null,
        layout: null,
        features: [
          feature('The main list', 'the things this app is about, with real content'),
          feature('Add and edit', 'a real write path, not a read-only page'),
          feature('Detail view', 'one item, in full'),
          feature('Search or filter', 'find one thing among many'),
          feature('A summary worth opening on', 'derived numbers that change as data changes'),
        ],
        screens: [
          screen('Home', 'the summary and the main list'),
          screen('Detail', 'one item in full'),
        ],
        charts: [chart('Overview', 'bars', 'the main quantity over recent periods')],
        collects: null,
        seedNote: '6-12 realistic records so the app opens full',
        constraints: [],
        rules: null,
        manages: [],
      };
  }
}

/** A complete plan from the idea alone — no network, no model, under a millisecond. */
export function scaffoldFor(idea: string): AppPlan {
  const type = classifyIdea(idea);
  return { type, appName: nameFromIdea(idea), ...baseFor(type) };
}
